/**
 * The cockpit's prioritisation state: the portfolio, the roster, the levers and the support plan.
 *
 * ⚠️ CALL IT THROUGH `RolePrioritisationProvider`, never directly. One instance serves the Company
 * View, the Cluster View's Balance the load and Planning's effort-model editor; a second copy would
 * agree with the first only for as long as every write round-tripped through localStorage before
 * the next component mounted, which is a race that has already been observed losing a lead's work.
 * The provider's own docblock has the whole story.
 *
 * THE PORTFOLIO IS LIVE, the working models are not. Open roles and their priority ratings come from
 * Airtable `hiring_plan` through the proxy; the effort model and the per-recruiter contracted hours
 * are still the seeded baseline, because no system holds the latter. Which source is on screen is
 * rendered, never implied — a lead has to know that before committing capacity against it.
 *
 * Local state (working models, intern allocation, out-of-office, support drafts, scenarios) lives in
 * THIS BROWSER only, the same way the Cluster View holds its capacity what-ifs. Every
 * write goes through `reconcileState`, because all of it is stored by cluster key or team index and
 * the live cluster list can change underneath it.
 *
 * ⚠️ A COMMITTED support allocation is the one thing that genuinely wants shared storage — a
 * commitment the other cluster leads cannot see is most of the point missing. Still open decision 3.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchLivePortfolio } from '../../../services/rolePrioritisationApi';
import {
  SEED_CLUSTERS,
  SEED_PORTFOLIO,
  type ClusterSeed,
  type LivePortfolio,
  type Portfolio,
  type PrioritisationState,
} from '../../../types/rolePrioritisation';
import {
  allClusterStats,
  companyStats,
  hoursLabel,
  suggestBalance,
  supportWarnings,
} from '../../../utils/rolePrioritisation';
import { commitmentExpired, hydrateState, reconcileState, withLogEntry } from '../../../utils/rolePrioritisationState';
import { fetchTeam } from '../../../services/taTeamApi';
import type { TeamMember } from '../../../types/taTeam';

const STATE_KEY = 'ta-role-prioritisation-v1';

/** Display order for the cluster cards. Live keys arrive in map order, which is not meaningful. */
const CLUSTER_ORDER = SEED_CLUSTERS.map((c) => c.key);
const byDisplayOrder = (a: ClusterSeed, b: ClusterSeed) => {
  const ai = CLUSTER_ORDER.indexOf(a.key);
  const bi = CLUSTER_ORDER.indexOf(b.key);
  return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
};

/**
 * The one read of the browser scratchpad. The merge itself lives in `hydrateState` beside
 * `reconcileState`: what a stored blob may become is a state concern, and keeping it here invited a
 * second opinion on validity in the view.
 */
function loadState(): PrioritisationState {
  try {
    return hydrateState(JSON.parse(localStorage.getItem(STATE_KEY) || 'null'));
  } catch {
    return hydrateState(null);
  }
}

/** The next cluster-lead weekly. Monday, unless today is Monday, in which case next Monday. */
function nextWeekly(now = new Date()): { label: string; iso: string } {
  const d = new Date(now);
  const daysAhead = (8 - d.getDay()) % 7 || 7;
  d.setDate(d.getDate() + daysAhead);
  d.setHours(23, 59, 59, 999);
  return {
    label: d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }),
    iso: d.toISOString(),
  };
}

/**
 * The cluster split, live where it can be. Falls back to the illustrative seed split, which
 * reconciles to the seeded portfolio, so the cluster view can never disagree with the total above it.
 */
function clustersFrom(live: LivePortfolio | null): { clusters: ClusterSeed[]; portfolio: Portfolio } {
  if (live?.source === 'airtable' && live.clusters?.length && live.portfolio) {
    return {
      clusters: live.clusters.map((c) => ({ key: c.key, roles: c.roles })).sort(byDisplayOrder),
      portfolio: live.portfolio,
    };
  }
  return { clusters: SEED_CLUSTERS, portfolio: SEED_PORTFOLIO };
}

/** Roles Airtable holds with no Approved Priority Rating: counted everywhere, priced nowhere. */
function unratedCount(live: LivePortfolio | null): number {
  return live?.source === 'airtable' ? (live.unrated?.length ?? 0) : 0;
}

function unratedByCluster(live: LivePortfolio | null): Record<string, number> {
  const out: Record<string, number> = {};
  if (live?.source !== 'airtable') return out;
  (live.clusters ?? []).forEach((c) => {
    out[c.key] = c.unrated;
  });
  return out;
}

/**
 * A shared-roster row in this tab's own terms.
 *
 * The two shapes differ on purpose: the roster says `kind: 'intern'` because that is what a person
 * IS, while this tab asks `isIntern` because that is the question its pool arithmetic puts. One
 * translation here rather than a second vocabulary spread through the maths.
 */
function toWorkingModel(m: TeamMember) {
  return {
    name: m.name,
    role: m.role,
    cluster: m.cluster,
    hours: m.hours,
    countsTowardHiring: m.countsTowardHiring,
    isIntern: m.kind === 'intern',
  };
}

/** Everything the three readers need. Mounted once, by the provider. */
export function useRolePrioritisation(userEmail?: string | null) {

  const [raw, setRaw] = useState<PrioritisationState>(() => loadState());
  const [live, setLive] = useState<LivePortfolio | null>(null);
  const [loadingLive, setLoadingLive] = useState(true);
  /** 'seed' until the shared roster arrives — the tab says which it is showing. */
  const [teamSource, setTeamSource] = useState<'shared' | 'seed'>('seed');

  /**
   * The TEAM comes from Planning's shared roster, not from this browser.
   *
   * ⚠️ Adopting it DROPS the support allocations when the people change. Allocations are stored by
   * team INDEX, so a roster with someone added, removed or re-ordered would silently re-point
   * "Anna lends 4 h to Growth" at whoever now sits at that index — a commitment attributed to a
   * colleague who never made it. Dropping them is the honest repair; `reconcileState` already
   * reports what it removed, and re-cutting them is the weekly's own job.
   */
  /**
   * ⚠️ RE-READABLE, and Planning calls it after a save (CodeRabbit, PR #417).
   *
   * The roster used to be read once per MOUNT, which was enough while every tab remounted the hook
   * — opening Role Prioritisation re-read it. With one provider above the tab strip, nothing
   * remounts for the whole cockpit session: saving the roster on Planning left the Company View
   * computing capacity from the team as it was when the page loaded, and the source bar still
   * saying "seeded baseline" after a real roster had just been saved. Lifting the state fixed one
   * staleness and introduced another; this is the other half.
   */
  const readTeam = useCallback(() => {
    let cancelled = false;
    fetchTeam()
      .then((stored) => {
        // An ARRAY check, not a length one: an empty roster is a real answer somebody saved, and
        // reading it as "nothing saved" would leave the seeded names on screen as though they were
        // the team.
        if (cancelled || !stored || !Array.isArray(stored.members)) return;
        const team = stored.members.map(toWorkingModel);
        setRaw((cur) => {
          const same =
            cur.team.length === team.length &&
            cur.team.every((p, i) => p.name === team[i].name);
          return { ...cur, team, allocations: same ? cur.allocations : [] };
        });
        setTeamSource('shared');
      })
      .catch(() => {
        // A roster that could not be read leaves the seeded names on screen, labelled as seeded.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => readTeam(), [readTeam]);

  const weekly = useMemo(() => nextWeekly(), []);

  const { clusters, portfolio } = useMemo(() => clustersFrom(live), [live]);
  const clusterKeys = useMemo(() => clusters.map((c) => c.key), [clusters]);

  /** Every write is repaired against the clusters and team that currently exist. */
  const reconciled = useMemo(() => reconcileState(raw, clusterKeys), [raw, clusterKeys]);
  const state = reconciled.state;
  // The assumptions are the RECONCILED ones, so nothing on screen is computed from a value the
  // reconciler rejected.
  const { model, cap } = state;

  /**
   * Every state write goes through here, and one that names what it changed is recorded in the log.
   * The child describes the edit; attribution is stamped here, where the signed-in identity is.
   */
  const applyState = useCallback(
    (next: PrioritisationState, note?: string) =>
      setRaw(note ? withLogEntry(next, userEmail, note) : next),
    [userEmail]
  );
  const applyEdit = useCallback(
    (fn: (s: PrioritisationState) => PrioritisationState, note: string) =>
      setRaw((s) => withLogEntry(fn(s), userEmail, note)),
    [userEmail]
  );

  const expired = useMemo(() => commitmentExpired(state), [state]);

  useEffect(() => {
    try {
      localStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch {
      // A full or blocked store must not break the view — this is a scratchpad, not a record.
    }
  }, [state]);

  /**
   * The first read. Deliberately NOT routed through the refresh handler: calling setState
   * synchronously inside an effect cascades renders (the lint rule that caught it), and the initial
   * load needs cancellation — without it an unmount mid-fetch sets state on a dead component.
   */
  useEffect(() => {
    let cancelled = false;
    fetchLivePortfolio(false).then((d) => {
      if (cancelled) return;
      setLive(d);
      setLoadingLive(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /** The Refresh button, which bypasses the proxy's 60s cache. */
  const refresh = useCallback(() => {
    setLoadingLive(true);
    fetchLivePortfolio(true)
      .then(setLive)
      .finally(() => setLoadingLive(false));
  }, []);

  const clusterStatsList = useMemo(
    () => allClusterStats(clusters, state, model, cap),
    [clusters, state, model, cap]
  );
  const company = useMemo(
    () => companyStats(clusters, state, model, cap, portfolio),
    [clusters, state, model, cap, portfolio]
  );
  const warnings = useMemo(
    () => supportWarnings(clusters, state, model, cap),
    [clusters, state, model, cap]
  );

  /** What is in force right now — the two levers that change the total, plus what was lent. */
  const supportSummary = useMemo(() => {
    const lent = state.allocations.reduce((s, a) => s + a.hours, 0);
    const bits: string[] = [];
    if (company.internHours > 0.005) bits.push(`+${hoursLabel(company.internHours)} h interns`);
    if (company.outHours > 0.005) bits.push(`−${hoursLabel(company.outHours)} h out of office`);
    if (lent > 0.005) bits.push(`${hoursLabel(lent)} h lent between clusters`);
    return bits.length ? bits.join(' · ') : null;
  }, [company.internHours, company.outHours, state.allocations]);

  const suggest = useCallback(() => {
    const { state: next, unresolved } = suggestBalance(clusters, state, model, cap);
    setRaw(next);
    return unresolved.length
      ? `${unresolved.join(', ')} still over and no donor has spare — that is a rating trade-off, not a support call.`
      : 'Every cluster now sits inside its capacity.';
  }, [clusters, state, model, cap]);

  return {
    state,
    raw,
    setRaw,
    live,
    loadingLive,
    teamSource,
    refresh,
    /** Re-reads the shared roster. Planning calls it after a save — see `readTeam`. */
    reloadTeam: readTeam,
    reconciled,
    clusterKeys,
    clusterStatsList,
    company,
    warnings,
    supportSummary,
    suggest,
    /** Pre-applied to the live payload, so no caller has to know what `source` means. */
    unratedCount: unratedCount(live),
    unratedByCluster: unratedByCluster(live),
    clusterPortfolios:
      live?.source === 'airtable' && live.clusters?.length
        ? live.clusters.map((c) => ({ key: c.key, roles: c.roles }))
        : undefined,
    applyState,
    applyEdit,
    expired,
    weekly,
    model,
    cap,
  };
}
